/** @jsxImportSource @emotion/react */
import { css, SerializedStyles } from '@emotion/react';
import { FC, Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, ButtonBase, ClickAwayListener, Popover } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { MobileDateTimePicker as DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { QSButton, QSButtonBase, QSIconButton, QSTypography } from '../../atoms';
import Logo from '../../../assets/logo';
import { BORDER_COLOR, HEADER_HEIGHT } from '../../../typings';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LoginStep, showLogoutModalAtom } from '../../../states/login';
import { getTrainingLink } from '../../../requests/organizations';
import useQuery from '../../../hooks/useQuery';
import { trainingLinkAtom } from '../../../states/training-link';
import {
  DEFAULT_TEAM,
  DT_ROADMAP_LINK,
  INACTIVE_STATUS_TIMER_SECONDS,
  MAIN_IN_SUPPORT_NUMBER,
} from '../../../constants';
import { WabaIntegrationMemberStatus } from '../../../typings/waba';
import { getUserStatus, setUserStatus } from '../../../requests/user';
import { USER_STATUS_STORAGE_KEYS } from '../../../constants/storage';
import { UserStatusData } from '../../../typings/user';
import { userStatusAtom } from '../../../states/user';
import { walletDetailsAtom } from '../../../states/wallet';
import { openPopup } from '../../../utils/popup';
import requestConfig from '../../../config/request';
import { getFirebaseToken } from '../../../../infra/auth/firebase-auth';
import { getUserChannelData } from '../../../../modules/login/v1/utility';
import QSTooltip from '../../molecules/tooltip';
import appConfig from '../../../config/app';
import LogoutModal from '../logout-modal';
import WarningIcon from '../../../icons/icons-revamp/warning';
import HelpIcon from '../../../icons/icons-revamp/help';
import WhatsNewBtn from './WhatsNewBtn';
import GetHelpPopover from './GetHelpPopover';
import HeadPhoneIcon from '../../../icons/HeadPhoneIcon';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

export type TrainingLinkDataType = {
  show: boolean;
  link: string;
};

interface HeaderProps {
  containerStyles?: SerializedStyles;
}

const Header: FC<HeaderProps> = ({ containerStyles }) => {
  const [open, setOpen] = useAtom(showLogoutModalAtom);
  const whatsNewBadgeRef = useRef<HTMLDivElement>(null);
  const [isHoveringOverWhatsNewButton, setIsHoveringOverWhatsNewButton] = useState(false);
  const showWhatsNewText = useRef(false);
  const [isUpdatePopupOpen, setisUpdatePopupOpen] = useState(false);
  const [dateTimePickerOpen, setDateTimePickerOpen] = useState(false);
  const [isOnlineStatusConfirmModalOpen, setIsOnlineStatusConfirmModalOpen] = useState(false);
  const [isOfflineStatusConfirmModalOpen, setIsOfflineStatusConfirmModalOpen] = useState(false);
  const [helpPopoverAnchorEl, setHelpPopoverAnchorEl] = useState<HTMLElement | null>(null);
  const shouldShowWelcomeBackUserStatus = useCallback(() => {
    const lastOfflineSyncedTime = localStorage.getItem(USER_STATUS_STORAGE_KEYS.LAST_OFFLINE_SYNCED_TIME);
    if (
      typeof lastOfflineSyncedTime === 'string' &&
      lastOfflineSyncedTime.length > 0 &&
      dayjs(lastOfflineSyncedTime).isValid()
    ) {
      if (dayjs(new Date()).diff(dayjs(lastOfflineSyncedTime), 'seconds') >= INACTIVE_STATUS_TIMER_SECONDS) {
        return true;
      }
    }
    return false;
  }, []);

  const {
    requestData: { data: trainingLinkData, loading: trainingLinkLoading },
  } = useQuery<TrainingLinkDataType>({
    requestAtom: trainingLinkAtom,
    queryFunction: getTrainingLink,
  });

  const userStatusGetter = useCallback(async () => {
    const userStatusData = await getUserStatus();
    if (
      shouldShowWelcomeBackUserStatus() &&
      typeof userStatusData?.status === 'string' &&
      userStatusData?.status.length > 0 &&
      userStatusData?.status !== WabaIntegrationMemberStatus.ACTIVE
    ) {
      setIsOnlineStatusConfirmModalOpen(true);
    }
    return userStatusData;
  }, [setIsOnlineStatusConfirmModalOpen]);

  const {
    requestData: { data: userStatusData, loading: userStatusLoading },
  } = useQuery<UserStatusData>({
    requestAtom: userStatusAtom,
    queryFunction: userStatusGetter,
  });

  const setUserStatusData = useSetAtom(userStatusAtom);

  const [teamMemberStatus, setTeamMemberStatus] = useState<{
    status: WabaIntegrationMemberStatus | null;
    statusExpiration: string | null;
  }>({
    status: userStatusData?.status ?? null,
    statusExpiration: userStatusData?.statusExpiration ?? null,
  });

  useEffect(() => {
    setTeamMemberStatus((prev) => ({
      ...prev,
      status: userStatusData?.status ?? null,
      statusExpiration: userStatusData?.statusExpiration ?? null,
    }));
  }, [userStatusData?.status, userStatusData?.statusExpiration]);

  useEffect(() => {
    if (typeof window !== 'undefined' && !!window.Canny)
      window.Canny('initChangelog', {
        appID: appConfig.cannyAppId,
        position: 'bottom',
        align: 'right',
        theme: 'light',
      });
  }, []);

  const handleCloseCannyChangelogPopup = () => {
    showWhatsNewText.current = false;
    if (typeof window !== 'undefined' && !!window.Canny) window.Canny('closeChangelog');
  };

  const channelData = getUserChannelData();
  const hideDTDetails = !!channelData?.name;

  const startUserStatusTimer = useCallback(() => {
    localStorage.setItem(USER_STATUS_STORAGE_KEYS.LAST_OFFLINE_SYNCED_TIME, new Date().toISOString());
  }, []);

  const stopUserStatusTimer = useCallback(() => {
    if (
      shouldShowWelcomeBackUserStatus() &&
      typeof userStatusData?.status === 'string' &&
      userStatusData?.status.length > 0 &&
      userStatusData?.status !== WabaIntegrationMemberStatus.ACTIVE
    ) {
      setIsOfflineStatusConfirmModalOpen(true);
    }
    localStorage.removeItem(USER_STATUS_STORAGE_KEYS.LAST_OFFLINE_SYNCED_TIME);
  }, [userStatusData?.status]);

  useEffect(() => {
    window.addEventListener('blur', startUserStatusTimer);
    return () => {
      window.removeEventListener('blur', startUserStatusTimer);
    };
  }, [startUserStatusTimer]);

  useEffect(() => {
    window.addEventListener('focus', stopUserStatusTimer);
    return () => {
      window.removeEventListener('focus', stopUserStatusTimer);
    };
  }, [stopUserStatusTimer]);

  const openDateTimePicker = () => {
    setDateTimePickerOpen(true);
  };

  const closeDateTimePicker = () => {
    setDateTimePickerOpen(false);
  };

  const { loading: walletLoading, data: walletData } = useAtomValue(walletDetailsAtom);

  const showLowBalanceBanner = useMemo(() => {
    if (!walletData || walletLoading) return false;
    switch (walletData.currencyCode) {
      case 'INR':
        return walletData.balance < 100;
      case 'USD':
        return walletData.balance < 10;
      case 'AED':
        return walletData.balance < 20;
      default:
        return walletData.balance < 10;
    }
  }, [walletData, walletLoading]);

  const handleOnClickRecharge = async () => {
    if (!walletData || walletLoading) return;
    if (walletData.generatePaymentLink) {
      const token = await getFirebaseToken();

      openPopup(`${requestConfig.dtCallbackUrl}/wallet?token=${token}`, '_self');
    } else window.open(walletData.rechargeLink, '_blank');
  };

  const renderLogoutModal = () => {
    return <LogoutModal open={open} onClose={() => setOpen(false)} />;
  };

  const renderDateTimePicker = () => {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DateTimePicker<Dayjs>
          ampm={true}
          ampmInClock={true}
          closeOnSelect={false}
          disabled={trainingLinkLoading || userStatusLoading}
          value={
            typeof teamMemberStatus.statusExpiration === 'string' &&
            teamMemberStatus.statusExpiration.length > 0 &&
            dayjs(teamMemberStatus.statusExpiration).isValid()
              ? dayjs(teamMemberStatus.statusExpiration)
              : null
          }
          onAccept={(statusExpiration) => {
            setTeamMemberStatus((prev) => ({
              ...prev,
              statusExpiration: statusExpiration ? statusExpiration.toISOString() : null,
            }));
          }}
          minDateTime={dayjs(new Date()).startOf('day')}
          css={css`
            display: none;
          `}
          slotProps={{
            dialog: {
              sx: { zIndex: '1600' },
            },
          }}
          open={dateTimePickerOpen}
          onOpen={openDateTimePicker}
          onClose={closeDateTimePicker}
        />
      </LocalizationProvider>
    );
  };

  const handleOnGetHelpClick = (event: React.MouseEvent<HTMLElement>) => {
    setHelpPopoverAnchorEl(event.currentTarget);
  };

  const handleCloseHelpPopover = () => {
    setHelpPopoverAnchorEl(null);
  };

  const handleHelpDocsClick = () => {
    let encodedMessage = encodeURIComponent('I want to learn more about DoubleTick');
    if (hideDTDetails) {
      if (channelData?.name) {
        encodedMessage = encodeURIComponent(`I want to learn more about ${channelData?.name}`);
      } else {
        encodedMessage = encodeURIComponent('I want to learn more');
      }
      openPopup(`https://wa.me/${channelData.supportNumber}?text=${encodedMessage}`);
    } else {
      openPopup(`https://wa.me/${MAIN_IN_SUPPORT_NUMBER}?text=${encodedMessage}`);
    }
    handleCloseHelpPopover();
  };

  const renderWhatsNewButtonContent = () => {
    let expand = isHoveringOverWhatsNewButton;
    if (whatsNewBadgeRef.current || showWhatsNewText.current) {
      const badge = whatsNewBadgeRef?.current?.querySelector('.Canny_Badge');
      if (badge || showWhatsNewText.current) {
        showWhatsNewText.current = true;
        expand = true;
      }
    }
    return <WhatsNewBtn expand={expand} setIsHoveringOverWhatsNewButton={setIsHoveringOverWhatsNewButton} />;
  };

  return (
    <Fragment>
      <Box
        css={[
          css`
            border-bottom: 1px solid rgba(224, 224, 234, 1);
            height: ${HEADER_HEIGHT}px;
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: space-between;
            column-gap: 16px;
            background: linear-gradient(68deg, rgb(255, 255, 255) 0%, rgb(242, 242, 242) 100%);
            padding: 0px 16px 0px 0px;
          `,
          containerStyles,
        ]}
      >
        <Box
          css={css`
            height: 100%;
            display: flex;
            justify-content: flex-start;
            align-items: center;
          `}
        >
          <Box
            css={css`
              margin: 4px 12px 0 18px;
            `}
          >
            <Logo height={35} width={'auto'} />
          </Box>
          <QSTypography
            sx={{
              fontSize: '20px',
              fontWeight: '650',
            }}
          >
            {channelData?.name ? channelData?.name : 'DoubleTick'}
          </QSTypography>
        </Box>

        <Box
          css={css`
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: flex-end;
            column-gap: 8px;
            position: relative;
          `}
        >
          {showLowBalanceBanner ? (
            <Box
              css={css`
                height: 32px;
                padding: 6px 8px;
                background: #f8eedf;
                box-shadow: none;
                border-radius: 4px;
                display: flex;
                gap: 6px;
                align-items: center;
              `}
            >
              <WarningIcon
                size={18}
                color="#E7951C"
                css={css`
                  margin-bottom: -2px;
                `}
              />
              <QSTypography
                css={css`
                  font-family: Inter;
                  font-size: 14px;
                  font-weight: 700;
                  line-height: 17px;
                  letter-spacing: 0px;
                  text-align: left;
                  color: #2d2d2d;
                `}
              >
                Your wallet balance is low
              </QSTypography>
              {!hideDTDetails ? (
                <QSButton
                  variant="text"
                  css={css`
                    font-family: Inter;
                    font-size: 14px;
                    font-weight: 600;
                    line-height: 17px;
                    letter-spacing: 0px;
                    text-align: left;
                    color: #3f906d;
                    text-decoration: underline;
                    &:hover {
                      text-decoration: underline;
                    }
                  `}
                  onClick={handleOnClickRecharge}
                >
                  Recharge now
                </QSButton>
              ) : null}
            </Box>
          ) : null}
          <Box
            css={css`
              display: flex;
              column-gap: 4px;
            `}
          >
            {!hideDTDetails && (
              <ClickAwayListener onClickAway={handleCloseCannyChangelogPopup}>
                <div
                  css={css`
                    overflow: hidden;
                    .Canny_Badge {
                      top: 5px;
                      right: 101px;
                      display: none;
                    }
                  `}
                >
                  <div data-canny-changelog="" ref={whatsNewBadgeRef}>
                    {renderWhatsNewButtonContent()}
                  </div>
                </div>
              </ClickAwayListener>
            )}
            <QSButton
              variant="text"
              startIcon={<HeadPhoneIcon width={20} height={20} color="#7A7A7A" />}
              onClick={handleOnGetHelpClick}
              css={css`
                border-radius: 4px;
                background: transparent;
                box-shadow: none;
                color: #2d2d2d;
                display: flex;
                align-items: center;
                justify-content: space-between;
                min-width: unset;
                .MuiButton-startIcon {
                  margin-left: auto;
                  margin-right: 4px;
                }
                &:hover {
                  background: rgba(0, 0, 0, 0.04);
                }
              `}
            >
              Support
            </QSButton>
          </Box>
        </Box>
      </Box>

      {renderDateTimePicker()}
      {renderLogoutModal()}

      <GetHelpPopover
        helpPopoverAnchorEl={helpPopoverAnchorEl}
        handleCloseHelpPopover={handleCloseHelpPopover}
        hideDTDetails={hideDTDetails}
      />
    </Fragment>
  );
};

export default Header;

// {/* TODO add in next release */}
// {/*<QSButtonBase*/}
// {/*  disabled={trainingLinkLoading || userStatusLoading}*/}
// {/*  onClick={() => setSelectStatusModalOpen(true)}*/}
// {/*  css={css`*/}
// {/*    height: 36px;*/}
// {/*    min-height: 36px;*/}
// {/*    max-height: 36px;*/}
// {/*    display: flex;*/}
// {/*    justify-content: center;*/}
// {/*    align-items: center;*/}
// {/*    column-gap: 12px;*/}
// {/*    box-shadow: 0px 1px 2px 0px rgba(97, 97, 97, 0.2);*/}
// {/*    background: rgba(251, 251, 252, 1);*/}
// {/*    color: #2d2d2d;*/}
// {/*    border-radius: 40px;*/}
// {/*    padding: 4px ${(typeof userStatusData?.status === 'string' && userStatusData?.status.length > 0) ? 12 : 6}px 4px 6px;*/}
// {/*  `}*/}
// {/*>*/}
// {/*  {userStatusLoading ? (*/}
// {/*    <Loader size={24} />*/}
// {/*  ) : (*/}
// {/*    <Avatar*/}
// {/*      css={css`*/}
// {/*        height: 24px;*/}
// {/*        min-height: 24px;*/}
// {/*        width: 24px;*/}
// {/*        min-width: 24px;*/}
// {/*        font-size: 14px;*/}
// {/*        font-weight: 600;*/}
// {/*        color: #2D2D2D;*/}
// {/*        background-color: ${getColorFromPhone({ phone: userDetails?.phone ?? '', name: userDetails?.name ?? '' })};*/}
// {/*        display: flex;*/}
// {/*        justify-content: center;*/}
// {/*        align-items: center;*/}
// {/*        flex: 0 0 auto;*/}
// {/*        text-transform: uppercase;*/}
// {/*      `}*/}
// {/*    >*/}
// {/*      {(userDetails?.name ?? '').replace(new RegExp(UNICODE_ZERO_WIDTH_REGEX), '').trim()[0]}*/}
// {/*    </Avatar>*/}
// {/*  )}*/}
// {/*  {typeof userStatusData?.status === 'string' && userStatusData?.status.length > 0 && (*/}
// {/*    <Fragment>*/}
// {/*      {!userStatusLoading && (*/}
// {/*        <div*/}
// {/*          css={css`*/}
// {/*            position: absolute;*/}
// {/*            top: 18px;*/}
// {/*            left: 18px;*/}
// {/*            color: inherit;*/}
// {/*            background-color: #FFFFFF;*/}
// {/*            border-radius: 50%;*/}
// {/*            height: 14px;*/}
// {/*            width: 14px;*/}
// {/*            padding: 3px;*/}
// {/*            display: flex;*/}
// {/*            justify-content: center;*/}
// {/*            align-items: center;*/}
// {/*            z-index: 1;*/}
// {/*          `}*/}
// {/*        >*/}
// {/*          {renderTeamMemberStatusIcon(userStatusData?.status, true, 12)}*/}
// {/*        </div>*/}
// {/*      )}*/}
// {/*      <div*/}
// {/*        css={css`*/}
// {/*          color: inherit;*/}
// {/*          background-color: transparent;*/}
// {/*          border-radius: 50%;*/}
// {/*          height: 16px;*/}
// {/*          width: 16px;*/}
// {/*          display: flex;*/}
// {/*          justify-content: center;*/}
// {/*          align-items: center;*/}
// {/*        `}*/}
// {/*      >*/}
// {/*        {renderTeamMemberStatusIcon(userStatusData?.status, false, 16)}*/}
// {/*      </div>*/}
// {/*    </Fragment>*/}
// {/*  )}*/}
// {/*</QSButtonBase>*/}
